import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Building2, ListOrdered } from "lucide-react";
import { OrderDialog } from "@/components/OrderDialog";
import { toast } from "sonner";

const emptyForm = { name: "", code: "", city: "", address: "", open_time: "", close_time: "" };

export default function Branches() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(new Set());
  const [ordering, setOrdering] = useState(false);

  const load = () => http.get("/branches").then((r) => { setItems(r.data); setSelected(new Set()); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...b }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.code) { toast.error("Ad ve kod zorunlu"); return; }
    try {
      const body = { ...form, open_time: form.open_time || null, close_time: form.close_time || null };
      if (editing) await http.put(`/branches/${editing.id}`, body);
      else await http.post("/branches", body);
      toast.success("Kaydedildi"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (b) => {
    await http.delete(`/branches/${b.id}`);
    toast.success("Silindi"); load();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      const { data } = await http.post("/branches/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} şube silindi`);
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((b) => b.id)));
  };

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Şubeler</h1>
          <p className="text-slate-400 text-sm mt-1">{items.length} şube</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button variant="destructive" onClick={bulkDelete} className="bg-red-600 hover:bg-red-500" data-testid="branch-bulk-delete">
              <Trash2 size={14} className="mr-1" /> {selected.size} Sil
            </Button>
          )}
          <Button variant="outline" className="border-slate-700" onClick={() => setOrdering(true)} data-testid="branch-order-btn">
            <ListOrdered size={15} className="mr-1" /> Sırala
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-500" data-testid="branch-new">
            <Plus size={16} className="mr-1" /> Yeni Şube
          </Button>
        </div>
      </header>
      {ordering && (
        <OrderDialog title="Şube Sıralaması" hint="Bu sıra Canlı Panel, Gün Sonu, Raporlar ve tüm listelerde kullanılır. Sürükleyin veya oklarla taşıyın."
                     items={items.map((b) => ({ id: b.id, label: b.name, sub: b.code }))} onClose={() => setOrdering(false)}
                     onSave={async (ids) => { try { await http.put("/order/branches", { ids }); toast.success("Şube sırası kaydedildi"); setOrdering(false); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); } }} />
      )}

      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-100">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="w-10">
                  <Checkbox checked={items.length > 0 && selected.size === items.length}
                            onCheckedChange={toggleAll} data-testid="branch-select-all" />
                </TableHead>
                <TableHead>Kod</TableHead>
                <TableHead>Ad</TableHead>
                <TableHead>Şehir</TableHead>
                <TableHead>Adres</TableHead>
                <TableHead>Açılış / Kapanış</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((b) => (
                <TableRow key={b.id} className="border-slate-800" data-testid={`branch-row-${b.code}`}>
                  <TableCell>
                    <Checkbox checked={selected.has(b.id)} onCheckedChange={() => toggleSelect(b.id)}
                              data-testid={`branch-select-${b.code}`} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{b.code}</TableCell>
                  <TableCell className="font-semibold">{b.name}</TableCell>
                  <TableCell className="text-sm">{b.city || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-400">{b.address || "-"}</TableCell>
                  <TableCell className="font-mono text-xs" data-testid={`branch-hours-${b.code}`}>{b.open_time || "--:--"} – {b.close_time || "--:--"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`branch-edit-${b.code}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(b)} data-testid={`branch-delete-${b.code}`}><Trash2 size={14} className="text-red-400" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10"><Building2 className="mx-auto mb-2" />Şube yok</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>{editing ? "Şube Düzenle" : "Yeni Şube"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kod</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                       disabled={!!editing} className="bg-slate-950/50 border-slate-700 font-mono" />
              </div>
              <div>
                <Label>Şehir</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                       className="bg-slate-950/50 border-slate-700" />
              </div>
            </div>
            <div>
              <Label>Ad</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="bg-slate-950/50 border-slate-700" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Açılış Saati</Label>
                <Input type="time" value={form.open_time || ""} onChange={(e) => setForm({ ...form, open_time: e.target.value })}
                       className="bg-slate-950/50 border-slate-700 font-mono" data-testid="branch-open-time" />
              </div>
              <div>
                <Label>Kapanış Saati</Label>
                <Input type="time" value={form.close_time || ""} onChange={(e) => setForm({ ...form, close_time: e.target.value })}
                       className="bg-slate-950/50 border-slate-700 font-mono" data-testid="branch-close-time" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-1">Açılıştan 30 dakika sonra açılış listesi doldurulmamışsa size ve şube müdürüne uyarı gider.</p>
            <div>
              <Label>Adres</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })}
                     className="bg-slate-950/50 border-slate-700" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">İptal</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="branch-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
